import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Star, 
  ThumbsUp, 
  ThumbsDown, 
  Download,
  Filter,
  RefreshCw,
  Building,
  User,
  Calendar,
  MessageSquare,
  Settings,
  Plus,
  Trash2,
  Save,
  Link,
  Target,
  Clock,
  DollarSign,
  Briefcase,
  AlertCircle,
  Check
} from 'lucide-react'
import toast from 'react-hot-toast'

const API_URL = (import.meta.env as any).VITE_API_URL || 'http://localhost:3001'

// API functions
const reviewsApi = {
  getServiceFlowReviews: async (params?: any) => {
    const response = await fetch(`${API_URL}/api/admin/reviews/serviceflow`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })
    return response.json()
  },
  getServiceFlowReviewStats: async () => {
    const response = await fetch(`${API_URL}/api/admin/reviews/serviceflow/stats`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })
    return response.json()
  },
  getConfig: async () => {
    const response = await fetch(`${API_URL}/api/admin/reviews/config`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })
    return response.json()
  },
  updateConfig: async (config: any) => {
    const response = await fetch(`${API_URL}/api/admin/reviews/config`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(config)
    })
    return response.json()
  }
}

interface Review {
  id: string
  sentiment: 'positive' | 'negative' | null
  rating: number | null
  feedback: string | null
  publicReview: string | null
  reviewerName: string | null
  reviewerTitle: string | null
  reviewerCompany: string | null
  reviewerImageUrl: string | null
  googleReviewed: boolean
  createdAt: string
  reviewerUser?: {
    firstName: string
    lastName: string
    email: string
  }
  reviewerBusiness?: {
    name: string
    industryType: string
  }
}

interface ReviewStats {
  totalReviews: number
  averageRating: number
  positiveCount: number
  negativeCount: number
  googleClickThroughRate: number
  ratingDistribution: {
    1: number
    2: number
    3: number
    4: number
    5: number
  }
}

interface Milestone {
  id: string
  type: 'days_active' | 'jobs_completed' | 'revenue_processed' | 'users_added'
  value: number
  label: string
  enabled: boolean
}

interface ReviewConfig {
  googleReviewUrl: string
  googlePlaceId: string
  enableMilestoneReviews: boolean
  milestones: Milestone[]
}

// Default milestones
const DEFAULT_MILESTONES: Milestone[] = [
  { id: '1', type: 'days_active', value: 30, label: 'First month active', enabled: true },
  { id: '2', type: 'jobs_completed', value: 10, label: '10 jobs completed', enabled: true },
  { id: '3', type: 'jobs_completed', value: 50, label: '50 jobs completed', enabled: true },
  { id: '4', type: 'jobs_completed', value: 100, label: '100 jobs completed', enabled: true },
  { id: '5', type: 'revenue_processed', value: 10000, label: '$10k revenue processed', enabled: true },
  { id: '6', type: 'revenue_processed', value: 50000, label: '$50k revenue processed', enabled: true },
  { id: '7', type: 'revenue_processed', value: 100000, label: '$100k revenue processed', enabled: true },
  { id: '8', type: 'users_added', value: 5, label: '5 team members added', enabled: false },
]

const ServiceFlowReviewsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'reviews' | 'settings'>('reviews')
  const [sentimentFilter, setSentimentFilter] = useState<string>('')
  const [minRating, setMinRating] = useState<number | ''>('')

  // Config state
  const [googleReviewUrl, setGoogleReviewUrl] = useState('')
  const [googlePlaceId, setGooglePlaceId] = useState('')
  const [enableMilestoneReviews, setEnableMilestoneReviews] = useState(true)
  const [milestones, setMilestones] = useState<Milestone[]>(DEFAULT_MILESTONES)
  const [configDirty, setConfigDirty] = useState(false)

  // Queries
  const { data: reviewsData, isLoading: reviewsLoading, refetch: refetchReviews } = useQuery(
    ['serviceflow-reviews', sentimentFilter, minRating],
    () => reviewsApi.getServiceFlowReviews({ sentiment: sentimentFilter || undefined, minRating: minRating || undefined }),
    { enabled: activeTab === 'reviews' }
  )

  const { data: statsData } = useQuery(
    'serviceflow-review-stats',
    reviewsApi.getServiceFlowReviewStats,
    { enabled: activeTab === 'reviews' }
  )

  const { data: configData, isLoading: configLoading } = useQuery(
    'serviceflow-review-config',
    reviewsApi.getConfig,
    { 
      enabled: activeTab === 'settings',
      onSuccess: (data) => {
        if (data.success && data.data) {
          setGoogleReviewUrl(data.data.googleReviewUrl || '')
          setGooglePlaceId(data.data.googlePlaceId || '')
          setEnableMilestoneReviews(data.data.enableMilestoneReviews ?? true)
          if (data.data.milestones && data.data.milestones.length > 0) {
            setMilestones(data.data.milestones)
          }
        }
      }
    }
  )

  // Save config mutation
  const saveConfigMutation = useMutation(
    (config: Partial<ReviewConfig>) => reviewsApi.updateConfig(config),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('Configuration saved successfully')
          setConfigDirty(false)
          queryClient.invalidateQueries('serviceflow-review-config')
        } else {
          toast.error(data.message || 'Failed to save configuration')
        }
      },
      onError: () => {
        toast.error('Failed to save configuration')
      }
    }
  )

  const reviews: Review[] = reviewsData?.data || []
  const stats: ReviewStats | null = statsData?.data || null

  // Export reviews as CSV
  const exportReviews = () => {
    const csvContent = [
      ['Date', 'Business', 'User', 'Sentiment', 'Rating', 'Feedback', 'Public Review', 'Google Clicked'].join(','),
      ...reviews.map(r => [
        new Date(r.createdAt).toLocaleDateString(),
        r.reviewerBusiness?.name || '',
        r.reviewerUser ? `${r.reviewerUser.firstName} ${r.reviewerUser.lastName}` : '',
        r.sentiment || '',
        r.rating || '',
        `"${(r.feedback || '').replace(/"/g, '""')}"`,
        `"${(r.publicReview || '').replace(/"/g, '""')}"`,
        r.googleReviewed ? 'Yes' : 'No'
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `serviceflow-reviews-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Render star rating
  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-gray-400">-</span>
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            size={16}
            className={i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    )
  }

  // Milestone handlers
  const addMilestone = () => {
    const newMilestone: Milestone = {
      id: Date.now().toString(),
      type: 'jobs_completed',
      value: 25,
      label: 'New milestone',
      enabled: true
    }
    setMilestones([...milestones, newMilestone])
    setConfigDirty(true)
  }

  const updateMilestone = (id: string, updates: Partial<Milestone>) => {
    setMilestones(milestones.map(m => m.id === id ? { ...m, ...updates } : m))
    setConfigDirty(true)
  }

  const deleteMilestone = (id: string) => {
    setMilestones(milestones.filter(m => m.id !== id))
    setConfigDirty(true)
  }

  const saveConfig = () => {
    saveConfigMutation.mutate({
      googleReviewUrl,
      googlePlaceId,
      enableMilestoneReviews,
      milestones
    })
  }

  const getMilestoneIcon = (type: string) => {
    switch (type) {
      case 'days_active': return <Clock size={16} />
      case 'jobs_completed': return <Briefcase size={16} />
      case 'revenue_processed': return <DollarSign size={16} />
      case 'users_added': return <User size={16} />
      default: return <Target size={16} />
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">ServiceFlow Pro Product Reviews</h1>
        <p className="text-gray-500 mt-1">
          Manage reviews and configure review request settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('reviews')}
            className={`pb-3 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'reviews'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Star size={16} />
              Reviews
            </div>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings size={16} />
              Configuration
              {configDirty && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
            </div>
          </button>
        </nav>
      </div>

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Star className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Reviews</p>
                    <p className="text-xl font-bold">{stats.totalReviews}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Star className="text-yellow-600 fill-yellow-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Average Rating</p>
                    <p className="text-xl font-bold">{stats.averageRating.toFixed(1)} / 5</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <ThumbsUp className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Positive</p>
                    <p className="text-xl font-bold text-green-600">{stats.positiveCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ThumbsDown className="text-red-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Negative</p>
                    <p className="text-xl font-bold text-red-600">{stats.negativeCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Google CTR</p>
                    <p className="text-xl font-bold">{stats.googleClickThroughRate.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rating Distribution */}
          {stats && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="font-semibold mb-4">Rating Distribution</h3>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution]
                  const total = Object.values(stats.ratingDistribution).reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? (count / total) * 100 : 0
                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="w-12 text-sm">{rating} star</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-yellow-400 h-4 rounded-full transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="w-12 text-sm text-right">{count}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-500" />
                <select
                  value={sentimentFilter}
                  onChange={e => setSentimentFilter(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="">All Sentiment</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Star size={16} className="text-gray-500" />
                <select
                  value={minRating}
                  onChange={e => setMinRating(e.target.value ? parseInt(e.target.value) : '')}
                  className="border rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4+ Stars</option>
                  <option value="3">3+ Stars</option>
                </select>
              </div>

              <div className="flex-1" />

              <button
                onClick={() => refetchReviews()}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
              >
                <RefreshCw size={16} />
                Refresh
              </button>

              <button
                onClick={exportReviews}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sentiment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Testimonial</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reviewsLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        Loading reviews...
                      </td>
                    </tr>
                  ) : reviews.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No reviews found
                      </td>
                    </tr>
                  ) : (
                    reviews.map(review => (
                      <tr key={review.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building size={16} className="text-gray-400" />
                            <div>
                              <p className="font-medium text-sm">
                                {review.reviewerBusiness?.name || 'Unknown Business'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {review.reviewerBusiness?.industryType || '-'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-gray-400" />
                            <div>
                              <p className="text-sm font-medium">
                                {review.reviewerName
                                  || (review.reviewerUser
                                    ? `${review.reviewerUser.firstName} ${review.reviewerUser.lastName}`
                                    : 'Anonymous')}
                              </p>
                              {(review.reviewerTitle || review.reviewerCompany) && (
                                <p className="text-xs text-gray-500">
                                  {[review.reviewerTitle, review.reviewerCompany].filter(Boolean).join(' · ')}
                                </p>
                              )}
                              {review.reviewerUser?.email && (
                                <p className="text-xs text-gray-500">{review.reviewerUser.email}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {review.reviewerImageUrl ? (
                            <img
                              src={review.reviewerImageUrl}
                              alt={review.reviewerName || 'Reviewer'}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {review.sentiment === 'positive' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                              <ThumbsUp size={12} />
                              Positive
                            </span>
                          ) : review.sentiment === 'negative' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                              <ThumbsDown size={12} />
                              Negative
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {renderStars(review.rating)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600 max-w-md truncate">
                            {review.publicReview || review.feedback || '-'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Calendar size={14} />
                            {new Date(review.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Google Review URL */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Link className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Google Review URL</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  When businesses indicate a positive experience with ServiceFlow Pro, they'll be redirected to this URL to leave a Google review.
                </p>
                <input
                  type="url"
                  value={googleReviewUrl}
                  onChange={(e) => {
                    setGoogleReviewUrl(e.target.value)
                    setConfigDirty(true)
                  }}
                  placeholder="https://g.page/r/YOUR_GOOGLE_PLACE_ID/review"
                  className="w-full border rounded-md px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Get your Google Review URL from your Google Business Profile
                </p>
                <label className="block text-sm font-medium text-gray-700 mt-5 mb-1">
                  Google Place ID (marketing ratings)
                </label>
                <input
                  type="text"
                  value={googlePlaceId}
                  onChange={(e) => {
                    setGooglePlaceId(e.target.value)
                    setConfigDirty(true)
                  }}
                  placeholder="ChIJ..."
                  className="w-full border rounded-md px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Used on serviceflow-pro.com for live star ratings and review schema.
                  Find it in Google Maps → share listing, or from a write-review URL that includes <code>placeid=</code>.
                </p>
              </div>
            </div>
          </div>

          {/* Milestone Reviews Toggle */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Target className="text-purple-600" size={24} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Milestone Review Requests</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Automatically request reviews when businesses reach certain milestones
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableMilestoneReviews}
                      onChange={(e) => {
                        setEnableMilestoneReviews(e.target.checked)
                        setConfigDirty(true)
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones Configuration */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Review Request Milestones</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Configure when to automatically request reviews from businesses
                </p>
              </div>
              <button
                onClick={addMilestone}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus size={16} />
                Add Milestone
              </button>
            </div>

            {!enableMilestoneReviews && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2 text-yellow-800 text-sm">
                <AlertCircle size={16} />
                Milestone reviews are currently disabled. Enable them above to use these settings.
              </div>
            )}

            <div className="space-y-3">
              {milestones.map((milestone) => (
                <div
                  key={milestone.id}
                  className={`border rounded-lg p-4 ${milestone.enabled ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Enable toggle */}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={milestone.enabled}
                        onChange={(e) => updateMilestone(milestone.id, { enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>

                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${milestone.enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      {getMilestoneIcon(milestone.type)}
                    </div>

                    {/* Type selector */}
                    <select
                      value={milestone.type}
                      onChange={(e) => updateMilestone(milestone.id, { type: e.target.value as Milestone['type'] })}
                      className="border rounded-md px-3 py-1.5 text-sm"
                      disabled={!milestone.enabled}
                    >
                      <option value="days_active">Days Active</option>
                      <option value="jobs_completed">Jobs Completed</option>
                      <option value="revenue_processed">Revenue Processed ($)</option>
                      <option value="users_added">Users Added</option>
                    </select>

                    {/* Value input */}
                    <div className="flex items-center gap-1">
                      {milestone.type === 'revenue_processed' && <span className="text-gray-500">$</span>}
                      <input
                        type="number"
                        value={milestone.value}
                        onChange={(e) => updateMilestone(milestone.id, { value: parseInt(e.target.value) || 0 })}
                        className="w-24 border rounded-md px-3 py-1.5 text-sm"
                        disabled={!milestone.enabled}
                        min={1}
                      />
                    </div>

                    {/* Label input */}
                    <input
                      type="text"
                      value={milestone.label}
                      onChange={(e) => updateMilestone(milestone.id, { label: e.target.value })}
                      className="flex-1 border rounded-md px-3 py-1.5 text-sm"
                      placeholder="Milestone label"
                      disabled={!milestone.enabled}
                    />

                    {/* Delete button */}
                    <button
                      onClick={() => deleteMilestone(milestone.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {milestones.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No milestones configured. Click "Add Milestone" to create one.
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                // Reset to saved values
                if (configData?.data) {
                  setGoogleReviewUrl(configData.data.googleReviewUrl || '')
                  setGooglePlaceId(configData.data.googlePlaceId || '')
                  setEnableMilestoneReviews(configData.data.enableMilestoneReviews ?? true)
                  setMilestones(configData.data.milestones || DEFAULT_MILESTONES)
                }
                setConfigDirty(false)
              }}
              disabled={!configDirty}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              onClick={saveConfig}
              disabled={!configDirty || saveConfigMutation.isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveConfigMutation.isLoading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Configuration
                </>
              )}
            </button>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 flex items-center gap-2">
              <AlertCircle size={16} />
              How Milestone Reviews Work
            </h4>
            <ul className="mt-2 text-sm text-blue-800 space-y-1 ml-6 list-disc">
              <li>When a business reaches a configured milestone, they receive an email inviting them to review ServiceFlow Pro</li>
              <li>Positive reviews (thumbs up) redirect to your Google Review page</li>
              <li>Negative feedback stays internal for you to review and improve</li>
              <li>Each milestone only triggers once per business</li>
              <li>A cron job runs daily to check for businesses reaching milestones</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default ServiceFlowReviewsPage
